import { DEFAULT_CHARACTER_ID } from "../engine/characterDefs";
import { WeaponVisual } from "../engine/weaponVisual";
import { STD_UI_MOVEMENT_TIME } from "./effectManager";

export type PlayerBodyState = "idle" | "walking";

export interface PlayerBodyFrame {
	filename: string;
	interval: number;
	bobOffsetX: number;
	bobOffsetY: number;
}

export type PlayerBodyFrameSets = Record<PlayerBodyState, PlayerBodyFrame[]>;

const IDLE_INTERVAL = 500;
// One full 2-frame stride per standard move tween.
const WALK_INTERVAL = STD_UI_MOVEMENT_TIME / 2;

// The body art has no arms — hands are drawn on the weapon overlays, so the overlay
// bobs with the chest drop in idle frame 2. Walk frame 2 only moves the legs.
function bodyFrameSets(artName: string): PlayerBodyFrameSets {
	return {
		idle: [
			{ filename: `PlayerBody_${artName}_Idle_1.png`, interval: IDLE_INTERVAL, bobOffsetX: 0, bobOffsetY: 0 },
			{ filename: `PlayerBody_${artName}_Idle_2.png`, interval: IDLE_INTERVAL, bobOffsetX: 0, bobOffsetY: 1 },
		],
		walking: [
			{ filename: `PlayerBody_${artName}_Walk_1.png`, interval: WALK_INTERVAL, bobOffsetX: 0, bobOffsetY: 0 },
			{ filename: `PlayerBody_${artName}_Walk_2.png`, interval: WALK_INTERVAL, bobOffsetX: 0, bobOffsetY: 0 },
		],
	};
}

const bodyFramesByCharacter: Record<string, PlayerBodyFrameSets> = {
	green: bodyFrameSets("Green"),
	blue: bodyFrameSets("Blue"),
	red: bodyFrameSets("Red"),
};

export function getPlayerBodyFrames(characterId: string, bodyState: PlayerBodyState): PlayerBodyFrame[] {
	const frameSets = bodyFramesByCharacter[characterId] ?? bodyFramesByCharacter[DEFAULT_CHARACTER_ID];
	return frameSets[bodyState];
}

// Record is deliberately total: a new WeaponVisual member fails typecheck until it
// gets art here.
const weaponOverlayByVisual: Record<WeaponVisual, string> = {
	[WeaponVisual.SMALL_GUN]: "PlayerWeapon_SmallGun.png",
	[WeaponVisual.CANNON]: "PlayerWeapon_Cannon.png",
	[WeaponVisual.LAUNCHER]: "PlayerWeapon_Launcher.png",
	[WeaponVisual.DEPLOYER]: "PlayerWeapon_Deployer.png",
	[WeaponVisual.HEAT_LAUNCHER]: "PlayerWeapon_HeatLauncher.png",
	[WeaponVisual.GRENADE]: "PlayerWeapon_Grenade.png",
	[WeaponVisual.CLEAVER]: "PlayerWeapon_Cleaver.png",
	[WeaponVisual.NONE]: "PlayerWeapon_Hands.png",
};

export function getPlayerWeaponOverlayFilename(visual: WeaponVisual): string {
	return weaponOverlayByVisual[visual];
}

export function getAllPlayerSpriteFilenames(): string[] {
	const filenames = new Set<string>();
	for (const frameSets of Object.values(bodyFramesByCharacter)) {
		for (const frames of Object.values(frameSets)) {
			for (const frame of frames) filenames.add(frame.filename);
		}
	}
	for (const filename of Object.values(weaponOverlayByVisual)) {
		filenames.add(filename);
	}
	return [...filenames];
}
